import { View, Pressable, Linking } from "react-native"
import { useLocalSearchParams, router, Link } from "expo-router"
import { SafeAreaView } from "react-native-safe-area-context"
import Icon from "@react-native-vector-icons/material-design-icons"

import { ar } from "i18n/ar"
import { cn, sharedStyles, trpc } from "@src/utils"
import { SupplierBalanceCard, SupplierBrandsCard, ActivityIndicator, Text, ScrollView, Header } from "@src/components"
import { Alert } from "@src/components/ui"

function SupplierInfoCard({ phones, notes, className }: { phones: string[]; notes?: string | null; className?: string }) {
  function handleCallPhone(phoneNumber: string) {
    Linking.openURL(`tel:${phoneNumber}`)
  }

  return (
    <View className={cn("rounded-lg border border-gray-200 bg-white shadow-sm", className)}>
      <View className="p-4 pb-2">
        <Text className="text-lg font-bold text-gray-800">{ar.information}</Text>
      </View>
      <View className="p-4 pt-2">
        {phones.length > 0 && (
          <>
            <Text className="text-sm font-semibold text-gray-600 mb-2">{ar.phone}</Text>
            {phones.map((phone) => (
              <Pressable
                key={phone}
                onPress={() => handleCallPhone(phone)}
                className="p-2 mb-2 flex-row items-center justify-between rounded border border-blue-200 active:opacity-70 bg-blue-50/50"
              >
                <Text className="font-bold p-1">{phone}</Text>
                <Icon name="phone" size={24} color="black" className="px-1" />
              </Pressable>
            ))}
          </>
        )}

        {notes && (
          <View className="mt-4">
            <Text className="mb-2 font-semibold text-gray-600">{ar.notes}</Text>
            <Text className="text-base text-gray-700 leading-6 bg-gray-50 p-3 rounded border border-gray-100">{notes}</Text>
          </View>
        )}
      </View>
    </View>
  )
}

export default function SupplierDetails() {
  const { id } = useLocalSearchParams<{ id: string }>()
  const supplierId = Number(id)
  const supplier = trpc.suppliers.get.useQuery({ id: supplierId })
  const utils = trpc.useUtils()
  const deactivate = trpc.suppliers.deactivate.useMutation()

  function handleDelete() {
    deactivate.mutate(
      { id: supplierId },
      {
        onSuccess: () => {
          utils.suppliers.list.invalidate()
          router.back()
        },
      },
    )
  }

  if (supplier.isLoading) {
    return (
      <>
        <Header title={ar.loading} />
        <ActivityIndicator className="mt-4" />
      </>
    )
  }

  const header = (
    <Header
      title={`#${supplier.data?.id} ${supplier.data?.name}`}
      right={
        <View className="h-full flex-row">
          <Link href={`/dashboard/supplier/${id}/payments/track`} asChild>
            <Pressable disabled={!supplier.data?.id} className="h-full justify-center px-2 disabled:opacity-50 active:opacity-70">
              <Icon name="history" size={22} color="#10B981" />
            </Pressable>
          </Link>

          <Link href={`/dashboard/supplier/${id}/edit`} asChild>
            <Pressable disabled={!supplier.data?.id} className="h-full justify-center px-2 disabled:opacity-50 active:opacity-70">
              <Icon name="pencil" size={22} />
            </Pressable>
          </Link>

          <Pressable
            disabled={!supplier.data?.id || deactivate.isPending}
            className="h-full justify-center px-2 disabled:opacity-50 active:opacity-70"
            onPress={() => {
              Alert.alert(ar.delete, ar.deleteSupplierConfirmation, [
                { text: ar.cancel, style: "cancel" },
                { text: ar.delete, style: "destructive", onPress: handleDelete },
              ])
            }}
          >
            <Icon name="delete" size={22} color="#EF4444" />
          </Pressable>
        </View>
      }
    />
  )

  if (supplier.data === undefined) {
    return (
      <SafeAreaView>
        <Text>{ar.somethingWentWrong}</Text>
      </SafeAreaView>
    )
  }

  return (
    <>
      {header}
      <ScrollView className="flex-1" contentContainerStyle={sharedStyles.contentContainer} onRefresh={supplier.refetch}>
        <SupplierBalanceCard supplierId={supplierId} balance={supplier.data.balance} className="mb-3" />

        <View className="mb-3 flex-row gap-3">
          <Pressable
            onPress={() => router.push(`/dashboard/transactions?supplierId=${id}`)}
            className="flex-1 bg-blue-600 rounded p-2.5 flex-row items-center justify-center shadow-md active:opacity-80"
          >
            <Icon name="receipt" size={22} color="white" />
            <Text className="text-white font-semibold text-base ml-2">{ar.transactions}</Text>
          </Pressable>

          <Pressable
            onPress={() => router.push(`/dashboard/payments?supplierId=${id}`)}
            className="flex-1 bg-green-600 rounded p-2.5 flex-row items-center justify-center shadow-md active:opacity-80"
          >
            <Icon name="credit-card" size={22} color="white" />
            <Text className="text-white font-semibold text-base ml-2">{ar.payments}</Text>
          </Pressable>
        </View>

        <SupplierInfoCard phones={supplier.data.phones} notes={supplier.data.notes} className="mb-3" />

        <SupplierBrandsCard brands={supplier.data.brands ?? []} />

        <SafeAreaView />
      </ScrollView>
    </>
  )
}
